import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";

const STORAGE_KEY = "coding_dashboard_date_filters_v2";

interface StoredDates {
  from: string | null;
  to: string | null;
}

export const getDefaultFromDate = (): Date => dayjs().subtract(2, "month").startOf("month").toDate();

const toDateString = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

const parseDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const parsed = dayjs(dateString);
  return parsed.isValid() ? parsed.toDate() : null;
};

const loadFromLocalStorage = (): StoredDates | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      parsed &&
      typeof parsed === "object" &&
      (parsed.from === null || typeof parsed.from === "string") &&
      (parsed.to === null || typeof parsed.to === "string")
    ) {
      return parsed as StoredDates;
    }
  } catch (error) {
    console.warn("Failed to parse date filters from localStorage:", error);
  }
  return null;
};

const getUrlParams = (): { from: string | null; to: string | null } => {
  const params = new URLSearchParams(window.location.search);
  return {
    from: params.get("from"),
    to: params.get("to"),
  };
};

const updateUrlParams = (fromDate: Date | null, toDate: Date | null) => {
  const params = new URLSearchParams(window.location.search);

  if (fromDate) {
    params.set("from", toDateString(fromDate));
  } else {
    params.delete("from");
  }

  if (toDate) {
    params.set("to", toDateString(toDate));
  } else {
    params.delete("to");
  }

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, "", newUrl);
};

export function useCodingDashboardDates() {
  // Initialize with defaults
  const [fromDate, setFromDateState] = useState<Date | null>(getDefaultFromDate());
  const [toDate, setToDateState] = useState<Date | null>(null);

  // Initialize from localStorage and URL params on mount
  useEffect(() => {
    const urlParams = getUrlParams();
    const storedDates = loadFromLocalStorage();

    // Priority: URL params > localStorage > defaults
    let initialFrom: Date | null = null;
    let initialTo: Date | null = null;

    if (urlParams.from) {
      initialFrom = parseDate(urlParams.from);
    } else if (storedDates?.from) {
      initialFrom = parseDate(storedDates.from);
    }

    if (urlParams.to) {
      initialTo = parseDate(urlParams.to);
    } else if (storedDates?.to) {
      initialTo = parseDate(storedDates.to);
    }

    // Fall back to defaults if parsing failed
    if (!initialFrom) {
      initialFrom = getDefaultFromDate();
    }

    setFromDateState(initialFrom);
    setToDateState(initialTo);
  }, []);

  // Sync to localStorage and URL when dates change
  useEffect(() => {
    const stored: StoredDates = {
      from: fromDate ? toDateString(fromDate) : null,
      to: toDate ? toDateString(toDate) : null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    updateUrlParams(fromDate, toDate);
  }, [fromDate, toDate]);

  const setFromDate = useCallback((date: Date | null) => {
    setFromDateState(date);
  }, []);

  const setToDate = useCallback((date: Date | null) => {
    setToDateState(date);
  }, []);

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
  };
}
